<script>
import { isWatermarkPlanEnabled, isProofPasswordPlanEnabled } from "../../utils/helpers";

export default {
  name: "proofToggles",
  props: ["proof", "margin"],
  data() {
    return {
      downloadFiles: false,
      approveIndividualFiles: false,
      viewAllComments: true,
      approveWithChanges: false,
      limitVersionVisibility: false,
      blockCommentingAfterProofDecision: false,
      watermarkEnabled: false,
      passwordProtected: false,
      newPassword: "",
    };
  },
  computed: {
    isWatermarkFeatureEnabled: function () {
      return isWatermarkPlanEnabled(this.$A.Auth);
    },
    isProofPasswordFeatureEnabled: function () {
      return isProofPasswordPlanEnabled(this.$A.Auth);
    },
    proofHasPassword: function () {
      return !!_.get(this, "$props.proof.hasPassword", false);
    },
  },
  mounted() {
    const userSettings = _.get(this, '$A.Auth.userSettings', {});
    if (this.$props.proof === undefined) {
      this.downloadFiles = !!userSettings.downloadFiles;
      this.approveIndividualFiles = !!userSettings.approveIndividualFiles;
      this.viewAllComments = !!userSettings.viewAllComments;
      this.approveWithChanges = !!userSettings.approveWithChanges;
      this.limitVersionVisibility = !!userSettings.limitVersionVisibility;
      this.blockCommentingAfterProofDecision = !!userSettings.blockCommentingAfterProofDecision;
      this.watermarkEnabled = this.isWatermarkFeatureEnabled && !!userSettings.watermarkEnabled;
    }
  },
  watch: {
    proof: function () {
      if (this.$props.proof !== undefined && this.$props.proof.id > 0) {
        this.downloadFiles = this.$props.proof.allowDownload;
        this.approveIndividualFiles = this.$props.proof.allowApproveIndividualFiles;
        this.viewAllComments = this.$props.proof.allowViewAllComments;
        this.approveWithChanges = this.$props.proof.allowApproveWithChanges;
        this.limitVersionVisibility = this.$props.proof.limitVersionVisibility;
        this.blockCommentingAfterProofDecision = this.$props.proof.blockCommentingAfterProofDecision;
        this.watermarkEnabled = this.isWatermarkFeatureEnabled && !!this.$props.proof.watermarkEnabled;
        this.passwordProtected = this.isProofPasswordFeatureEnabled && !!this.$props.proof.hasPassword;
      }
    },
    downloadFiles: function (newValue) {
      this.updateSettings();
    },
    approveIndividualFiles: function (newValue) {
      this.updateSettings();
    },
    viewAllComments: function (newValue) {
      this.updateSettings();
    },
    approveWithChanges: function (newValue) {
      this.updateSettings();
    },
    limitVersionVisibility: function (newValue) {
      this.updateSettings();
    },
    blockCommentingAfterProofDecision: function (newValue) {
      this.updateSettings();
    },
    watermarkEnabled: function (newValue) {
      this.updateSettings();
    },
    passwordProtected: function (newValue) {
      // Turning protection off removes the password; turning it on only reveals
      // the input — nothing is saved until the owner sets a password.
      if (!newValue && this.proofHasPassword) {
        this.updateSettings({ removePassword: true });
      }
      if (!newValue) {
        this.newPassword = "";
      }
    },
  },
  methods: {
    updateSettings: function (passwordChange) {
      if (this.$props.proof !== undefined && this.$props.proof.id > 0) {
        let d = {
          allowDownload: this.downloadFiles,
          allowApproveIndividualFiles: this.approveIndividualFiles,
          allowViewAllComments: this.viewAllComments,
          allowApproveWithChanges: this.approveWithChanges,
          limitVersionVisibility: this.limitVersionVisibility,
          blockCommentingAfterProofDecision: this
              .blockCommentingAfterProofDecision,
          watermarkEnabled: this.isWatermarkFeatureEnabled && this.watermarkEnabled,
          ...(passwordChange || {}),
        };
        this.$root.$emit("proof::save::settings", d);
      }
    },
    savePassword: function () {
      if (this.newPassword.length === 0) {
        this.$A.AlertUser("Please Enter A Proof Password", "warning", 10000);
        return;
      }
      this.updateSettings({ password: this.newPassword });
      this.newPassword = "";
    },
    getCurrentSettings: function () {
      return {
        downloadFiles: this.downloadFiles,
        approveIndividualFiles: this.approveIndividualFiles,
        viewAllComments: this.viewAllComments,
        approveWithChanges: this.approveWithChanges,
        limitVersionVisibility: this.limitVersionVisibility,
        blockCommentingAfterProofDecision: this
            .blockCommentingAfterProofDecision,
        watermarkEnabled: this.isWatermarkFeatureEnabled && this.watermarkEnabled,
      };
    },
  },
};

</script>

<template>
  <div class="d-flex flex-column gap-10">
    <!--Toggle -->
    <div class="ashore-toggle d-flex gap-2" id="proof_toggle_download">
      <label class="switch mb-0 opacity-100">
        <input type="checkbox" v-model="downloadFiles" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Download Files</p>
        <label>
          <img
            id="proof_toggle_downloadfiles_icon"
            class="info-icon"
            src="/img/icon-info.svg"
          />
          <b-tooltip target="proof_toggle_downloadfiles_icon" triggers="hover">
            Turn this toggle on to allow approvers to download the files in your
            proof.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div class="ashore-toggle d-flex gap-2" id="proof_toggle_approve_ind_files">
      <label class="switch mb-0 opacity-100">
        <input type="checkbox" v-model="approveIndividualFiles" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Approve Individual Files</p>
        <label>
          <img
            id="proof_toggle_approveindiviualfiles_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip
            target="proof_toggle_approveindiviualfiles_icon"
            triggers="hover"
          >
            Turn this toggle on to require approvers to make an approval
            decision on each file in your proof. Otherwise, we'll ask for one
            approval decision for all files in this proof.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div class="ashore-toggle d-flex gap-2" id="proof_toggle_comments">
      <label class="switch mb-0 opacity-100">
        <input type="checkbox" v-model="viewAllComments" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">View All Comments</p>
        <label>
          <img
            id="proof_toggle_viewallcomments_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip
            target="proof_toggle_viewallcomments_icon"
            triggers="hover"
          >
            Turn this toggle on to allow your approvers and commenters to see,
            interact with, and be notified about other comments on a proof.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div class="ashore-toggle d-flex gap-2" id="proof_toggle_changes">
      <label class="switch mb-0 opacity-100">
        <input type="checkbox" v-model="approveWithChanges" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Approve With Changes</p>
        <label>
          <img
            id="proof_toggle_approvewithchanges_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip
            target="proof_toggle_approvewithchanges_icon"
            triggers="hover"
          >
            Turn this toggle on to allow your approvers to say, "make these
            changes but I don't need to see a new version". We'll treat the
            proof as approved.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div
      class="ashore-toggle d-flex gap-2"
      id="proof_toggle_limitVersionVisibility"
    >
      <label class="switch mb-0 opacity-100">
        <input type="checkbox" v-model="limitVersionVisibility" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Limit Version Visibility</p>
        <label>
          <img
            id="proof_toggle_limitVersionVisibility_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip
            target="proof_toggle_limitVersionVisibility_icon"
            triggers="hover"
          >
            Turn this toggle on to prevent approvers from viewing proof versions
            sent to approvers in other workflow stages.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div
      class="ashore-toggle d-flex gap-2"
      id="proof_toggle_blockCommentingAfterProofDecision"
    >
      <label :class="'switch mb-0 opacity-100 ' + margin">
        <input type="checkbox" v-model="blockCommentingAfterProofDecision" />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Block Commenting After Decision</p>
        <label>
          <img
            id="proof_toggle_blockCommentingAfterProofDecision_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip
            target="proof_toggle_blockCommentingAfterProofDecision_icon"
            triggers="hover"
          >
            Turn this toggle on to prevent approvers from submitting comments
            after making a decision.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div
      class="ashore-toggle d-flex gap-2"
      :class="{ 'watermark-locked': !isWatermarkFeatureEnabled }"
      id="proof_toggle_watermark"
      @click="isWatermarkFeatureEnabled ? null : $A.BuyNow()"
    >
      <label class="switch mb-0 opacity-100">
        <input
          type="checkbox"
          v-model="watermarkEnabled"
          :disabled="!isWatermarkFeatureEnabled"
        />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Watermark Files</p>
        <span
          v-if="!isWatermarkFeatureEnabled"
          id="proof_toggle_watermark_upgrade"
          class="watermark-upgrade-badge"
        >Upgrade</span>
        <b-tooltip
          v-if="!isWatermarkFeatureEnabled"
          target="proof_toggle_watermark_upgrade"
          triggers="hover"
        >
          Watermarking is available on Standard and Premium plans. Click to
          upgrade.
        </b-tooltip>
        <label v-if="isWatermarkFeatureEnabled">
          <img
            id="proof_toggle_watermark_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip target="proof_toggle_watermark_icon" triggers="hover">
            Turn this toggle on to stamp your account logo on every page of
            PDFs uploaded to this proof. Only applies to newly uploaded PDFs
            &mdash; files uploaded before turning this on are not affected.
          </b-tooltip>
        </label>
      </div>
    </div>
    <!--/Toggle -->
    <!--Toggle -->
    <div
      class="ashore-toggle d-flex gap-2"
      :class="{ 'watermark-locked': !isProofPasswordFeatureEnabled }"
      id="proof_toggle_password"
      @click="isProofPasswordFeatureEnabled ? null : $A.BuyNow()"
    >
      <label class="switch mb-0 opacity-100">
        <input
          type="checkbox"
          v-model="passwordProtected"
          :disabled="!isProofPasswordFeatureEnabled"
        />
        <div class="slider round"></div>
      </label>
      <div class="d-flex gap-10">
        <p class="text-break mb-0">Password Protection</p>
        <span
          v-if="!isProofPasswordFeatureEnabled"
          id="proof_toggle_password_upgrade"
          class="watermark-upgrade-badge"
        >Upgrade</span>
        <b-tooltip
          v-if="!isProofPasswordFeatureEnabled"
          target="proof_toggle_password_upgrade"
          triggers="hover"
        >
          Password protection is available on Standard and Premium plans.
          Click to upgrade.
        </b-tooltip>
        <label v-if="isProofPasswordFeatureEnabled">
          <img
            id="proof_toggle_password_icon"
            src="/img/icon-info.svg"
            class="info-icon"
          />
          <b-tooltip target="proof_toggle_password_icon" triggers="hover">
            Turn this toggle on to require approvers to enter a password
            before they can open this proof. This also applies to links sent
            through workflows.
          </b-tooltip>
        </label>
      </div>
    </div>
    <div
      v-if="isProofPasswordFeatureEnabled && passwordProtected"
      class="d-flex gap-2 password-editor"
      id="proof_password_editor"
    >
      <input
        type="password"
        class="form-control"
        v-model="newPassword"
        :placeholder="proofHasPassword ? 'Enter new password' : 'Enter a password'"
        autocomplete="new-password"
        @keyup.enter="savePassword()"
      />
      <button
        class="password-save-btn"
        id="proof_password_save_btn"
        @click="savePassword()"
      >
        {{ proofHasPassword ? "Update" : "Set" }}
      </button>
    </div>
    <!--/Toggle -->
  </div>
</template>

<style scoped>

.info-icon {
  height: 18px;
}

.ashore-toggle .switch {
  position: relative !important;
  display: inline-block !important;
  width: 44px !important;
  height: 24px !important;
  margin-right: 8px !important;
}

.watermark-locked {
  cursor: pointer;
}
.watermark-locked .switch {
  opacity: 0.4 !important;
  cursor: not-allowed !important;
}
.watermark-locked .switch .slider {
  background-color: #d1d5db !important;
}
.watermark-locked p {
  color: #9ca3af;
}
.watermark-upgrade-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  background: #019256;
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.watermark-locked:hover .watermark-upgrade-badge {
  background: #017a48;
}

.password-editor .form-control {
  height: 34px;
  font-size: 13px;
}
.password-save-btn {
  white-space: nowrap;
  padding: 6px 14px;
  height: 34px;
  background: #019256;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.password-save-btn:hover {
  background: #017a48;
}

</style>
